import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Search, MapPin, GraduationCap, Building2, Calendar } from 'lucide-react';
import { mockData } from '../data/mockData';
import { useAuth } from '../context/AuthContext';
import { useAuthGate } from '../context/AuthGateContext';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

const SearchSection = () => {
  const [searchType, setSearchType] = useState('city');
  const [searchQuery, setSearchQuery] = useState('');
  const [moveInDate, setMoveInDate] = useState(null);
  const [moveOutDate, setMoveOutDate] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { requireAuth } = useAuthGate();

  const handleSearch = (e) => {
    e.preventDefault();
    if (!user) {
      requireAuth('/search-results');
      return;
    }
    if (searchQuery) {
      navigate(`/search-results?type=${searchType}&q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion);
    setShowSuggestions(false);
  };

  const filteredSuggestions = (mockData.searchSuggestions[searchType] || [])
    .filter(item => item.toLowerCase().includes(searchQuery.toLowerCase()))
    .slice(0, 8);

  const tabBtnClass = (active) =>
    `rounded-full px-6 py-3 transition-all duration-300 font-medium text-sm ${
      active ? 'brand-gradient-bg text-white shadow-sm' : 'text-gray-600 hover:text-[#094f6f]'
    }`;

  const tabs = [
    { id: 'city', icon: MapPin, label: 'By City' },
    { id: 'university', icon: GraduationCap, label: 'By University' },
    { id: 'property', icon: Building2, label: 'By Property' },
  ];

  return (
    <section className="py-20 white-red-gradient">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold gradient-text mb-4">
            Find Your Perfect Accommodation
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Search through thousands of verified properties across Australia
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Search Type Tabs */}
          <div className="flex flex-wrap justify-center mb-8 bg-white rounded-full p-2 shadow-lg border border-gray-100">
            {tabs.map(({ id, icon: Icon, label }) => (
              <Button key={id} type="button" variant="ghost"
                className={tabBtnClass(searchType === id)}
                onClick={() => { setSearchType(id); setSearchQuery(''); setShowSuggestions(false); }}>
                <Icon className="w-4 h-4 mr-2" />
                {label}
              </Button>
            ))}
          </div>

          <form onSubmit={handleSearch}>
            <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 space-y-5">

              {/* Search Input */}
              <div className="relative">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {searchType === 'city' ? 'City / Suburb' : searchType === 'university' ? 'University or College' : 'Property Name'}
                </label>
                <div className="relative">
                  <Input
                    value={searchQuery}
                    onChange={e => { setSearchQuery(e.target.value); setShowSuggestions(true); }}
                    onFocus={() => setShowSuggestions(true)}
                    onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                    placeholder={
                      searchType === 'city' ? 'e.g. Melbourne CBD, Sydney...'
                      : searchType === 'university' ? 'e.g. University of Melbourne, UNSW...'
                      : 'e.g. Scape, Iglu, UniLodge...'
                    }
                    className="pl-10 h-12 border-gray-200 focus:border-[#094f6f]"
                  />
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                </div>
                {showSuggestions && searchQuery && filteredSuggestions.length > 0 && (
                  <div className="absolute z-50 bg-white border border-gray-200 rounded-xl shadow-xl mt-1 w-full max-h-56 overflow-y-auto">
                    {filteredSuggestions.map((s, i) => (
                      <div key={i} className="px-4 py-3 hover:bg-sky-50 cursor-pointer text-sm text-gray-700 flex items-center gap-2 border-b border-gray-50 last:border-0"
                        onMouseDown={() => handleSuggestionClick(s)}>
                        {searchType === 'university' ? <GraduationCap className="w-3.5 h-3.5 text-[#094f6f] flex-shrink-0" /> : <MapPin className="w-3.5 h-3.5 text-[#094f6f] flex-shrink-0" />}
                        {s}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Dates row */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Move-in &amp; Move-out Dates</label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="relative">
                    <DatePicker
                      selected={moveInDate}
                      onChange={date => { setMoveInDate(date); setSelectedDuration(''); if (moveOutDate && date > moveOutDate) setMoveOutDate(null); }}
                      minDate={new Date()}
                      placeholderText="Move-in date"
                      dateFormat="dd/MM/yyyy"
                      className="w-full pl-10 h-12 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30 focus:border-[#094f6f]"
                      wrapperClassName="w-full"
                      calendarClassName="shadow-xl rounded-xl border-0"
                      showMonthDropdown showYearDropdown dropdownMode="select"
                    />
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                  <div className="relative">
                    <DatePicker
                      selected={moveOutDate}
                      onChange={date => { setMoveOutDate(date); setSelectedDuration(''); }}
                      minDate={moveInDate || new Date()}
                      placeholderText="Move-out date"
                      dateFormat="dd/MM/yyyy"
                      className="w-full pl-10 h-12 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#094f6f]/30 focus:border-[#094f6f]"
                      wrapperClassName="w-full"
                      calendarClassName="shadow-xl rounded-xl border-0"
                      showMonthDropdown showYearDropdown dropdownMode="select"
                    />
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </div>

              {/* OR divider */}
              <div className="flex items-center gap-4">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-xs font-bold text-gray-400 bg-white px-3 py-1 rounded-full border border-gray-200">OR</span>
                <div className="flex-1 border-t border-gray-200" />
              </div>

              {/* Duration + Search button */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Stay Duration</label>
                  <Select value={selectedDuration} onValueChange={v => { setSelectedDuration(v); setMoveInDate(null); setMoveOutDate(null); }}>
                    <SelectTrigger className="h-12 border-gray-200 focus:border-[#094f6f]">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      {['Short Term (1-3 months)', '1 Semester (4-6 months)', '2 Semesters (8-10 months)', '1 Academic Year', '2 Years', 'Flexible'].map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex items-end">
                  <Button type="submit" className="w-full h-12 brand-gradient-bg text-white hover:opacity-90 transition-opacity font-semibold">
                    <Search className="w-4 h-4 mr-2" />
                    {user ? 'Search Properties' : 'Sign In to Search'}
                  </Button>
                </div>
              </div>
            </div>
          </form>

          {/* Quick Filters */}
          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm mb-3">Popular searches:</p>
            <div className="flex flex-wrap justify-center gap-2">
              {mockData.quickFilters.map((filter, i) => (
                <button key={i} type="button"
                  className="rounded-full border border-[#094f6f]/30 text-[#094f6f] bg-white px-3 py-1.5 text-xs hover:bg-sky-50 transition-colors"
                  onClick={() => { setSearchQuery(filter); setShowSuggestions(false); }}>
                  {filter}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SearchSection;
